import React, { useState, useEffect } from 'react';
import { Users, Search, Edit2, Trash2, Plus, UserPlus, UserMinus } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuthStore } from '../../stores/authStore';

interface UserGroup {
  id: string;
  nombre: string;
  descripcion: string | null;
  created_at: string;
  role: 'administrador' | 'usuario_avanzado' | 'usuario_estandar';
  usuarios_count?: number;
}

interface User {
  id: string;
  email: string;
  nombre: string;
  apellidos: string;
  grupo_id: string;
}

export default function UserGroups() {
  const { user: currentUser } = useAuthStore();
  const [groups, setGroups] = useState<UserGroup[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showUsersModal, setShowUsersModal] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<UserGroup | null>(null);
  const [groupUsers, setGroupUsers] = useState<User[]>([]);
  const [availableUsers, setAvailableUsers] = useState<User[]>([]);
  const [newGroup, setNewGroup] = useState({
    nombre: '',
    descripcion: '',
    role: 'usuario_estandar' as const
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (currentUser) {
      fetchGroups();
    }
  }, [currentUser]);

  const fetchGroups = async () => {
    try {
      const { data: groups, error } = await supabase
        .from('grupos_usuarios')
        .select(`
          *,
          usuarios_count:usuarios(count)
        `)
        .order('nombre');

      if (error) throw error;

      const transformedGroups = groups?.map(group => ({
        ...group,
        usuarios_count: group.usuarios_count?.[0]?.count || 0
      }));

      setGroups(transformedGroups || []);
    } catch (error) {
      console.error('Error fetching groups:', error);
      setError('Error al cargar los grupos');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchGroupUsers = async (groupId: string) => {
    try {
      const { data: users, error } = await supabase
        .from('usuarios')
        .select('id, email, nombre, apellidos, grupo_id')
        .eq('grupo_id', groupId);

      if (error) throw error;
      setGroupUsers(users || []);

      const { data: allUsers, error: allUsersError } = await supabase
        .from('usuarios')
        .select('id, email, nombre, apellidos, grupo_id')
        .neq('grupo_id', groupId)
        .order('nombre');

      if (allUsersError) throw allUsersError;
      setAvailableUsers(allUsers || []);
    } catch (error) {
      console.error('Error fetching group users:', error);
      setError('Error al cargar los usuarios del grupo');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      if (selectedGroup) {
        const { error } = await supabase
          .from('grupos_usuarios')
          .update({
            nombre: newGroup.nombre,
            descripcion: newGroup.descripcion || null,
            role: newGroup.role
          })
          .eq('id', selectedGroup.id);

        if (error) throw error;
        setSuccess('Grupo actualizado correctamente');
      } else {
        const { error } = await supabase
          .from('grupos_usuarios')
          .insert([{
            nombre: newGroup.nombre,
            descripcion: newGroup.descripcion || null,
            role: newGroup.role
          }]);

        if (error) throw error;
        setSuccess('Grupo creado correctamente');
      }

      setShowModal(false);
      setSelectedGroup(null);
      setNewGroup({ nombre: '', descripcion: '', role: 'usuario_estandar' });
      fetchGroups();
    } catch (error: any) {
      setError(error.message);
    }
  };

  const handleAddUserToGroup = async (userId: string, groupId: string) => {
    try {
      const { error } = await supabase
        .from('usuarios')
        .update({ grupo_id: groupId })
        .eq('id', userId);

      if (error) throw error;
      setSuccess('Usuario añadido al grupo correctamente');
      fetchGroupUsers(groupId);
      fetchGroups();
    } catch (error: any) {
      setError(error.message);
    }
  };

  const handleRemoveUserFromGroup = async (userId: string, groupId: string) => {
    try {
      const { data: defaultGroup, error: defaultGroupError } = await supabase
        .from('grupos_usuarios')
        .select('id')
        .eq('nombre', 'Grupo Estándar')
        .single();

      if (defaultGroupError) throw defaultGroupError;
      if (!defaultGroup) throw new Error('No se encontró el grupo por defecto');

      const { error } = await supabase
        .from('usuarios')
        .update({ grupo_id: defaultGroup.id })
        .eq('id', userId);

      if (error) throw error;
      setSuccess('Usuario removido del grupo correctamente');
      fetchGroupUsers(groupId);
      fetchGroups();
    } catch (error: any) {
      setError(error.message);
    }
  };

  const handleManageUsers = (group: UserGroup) => {
    setSelectedGroup(group);
    fetchGroupUsers(group.id);
    setShowUsersModal(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('¿Estás seguro de eliminar este grupo? Los usuarios serán movidos al grupo por defecto.')) {
      return;
    }

    try {
      const { data: defaultGroup, error: defaultGroupError } = await supabase
        .from('grupos_usuarios')
        .select('id')
        .eq('nombre', 'Grupo Estándar')
        .single();

      if (defaultGroupError) throw defaultGroupError;
      if (!defaultGroup) throw new Error('No se encontró el grupo por defecto');

      const { error: updateError } = await supabase
        .from('usuarios')
        .update({ grupo_id: defaultGroup.id })
        .eq('grupo_id', id);

      if (updateError) throw updateError;

      const { error } = await supabase
        .from('grupos_usuarios')
        .delete()
        .eq('id', id);

      if (error) throw error;
      setSuccess('Grupo eliminado correctamente');
      fetchGroups();
    } catch (error: any) {
      setError(error.message);
    }
  };

  const handleEdit = (group: UserGroup) => {
    setSelectedGroup(group);
    setNewGroup({
      nombre: group.nombre,
      descripcion: group.descripcion || '',
      role: group.role
    });
    setShowModal(true);
  };

  const filteredGroups = groups.filter(group =>
    group.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (group.descripcion?.toLowerCase() || '').includes(searchTerm.toLowerCase())
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Gestión de Grupos de Usuarios</h1>
        <button 
          onClick={() => {
            setSelectedGroup(null);
            setNewGroup({ nombre: '', descripcion: '', role: 'usuario_estandar' });
            setShowModal(true);
          }}
          className="flex items-center px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
        >
          <Plus className="w-5 h-5 mr-2" />
          Añadir Grupo
        </button>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {success && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
          {success}
        </div>
      )}

      <div className="bg-white rounded-lg shadow-md">
        <div className="p-6">
          <div className="flex items-center mb-4">
            <div className="relative flex-1">
              <input
                type="text"
                placeholder="Buscar grupos..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-left border-b">
                  <th className="pb-3 font-semibold text-gray-600">Nombre</th>
                  <th className="pb-3 font-semibold text-gray-600">Descripción</th>
                  <th className="pb-3 font-semibold text-gray-600">Rol</th>
                  <th className="pb-3 font-semibold text-gray-600">Usuarios</th>
                  <th className="pb-3 font-semibold text-gray-600">Creado</th>
                  <th className="pb-3 font-semibold text-gray-600">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filteredGroups.map((group) => (
                  <tr key={group.id} className="border-b">
                    <td className="py-3">
                      <div className="flex items-center">
                        <Users className="w-5 h-5 text-blue-500 mr-2" />
                        {group.nombre}
                      </div>
                    </td>
                    <td className="py-3">{group.descripcion || '-'}</td>
                    <td className="py-3">
                      <span className={`px-2 py-1 rounded-full text-sm ${
                        group.role === 'administrador' 
                          ? 'bg-purple-100 text-purple-800'
                          : group.role === 'usuario_avanzado'
                          ? 'bg-blue-100 text-blue-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {group.role === 'administrador' 
                          ? 'Administrador'
                          : group.role === 'usuario_avanzado'
                          ? 'Usuario Avanzado'
                          : 'Usuario Estándar'}
                      </span>
                    </td>
                    <td className="py-3">
                      <button
                        onClick={() => handleManageUsers(group)}
                        className="flex items-center text-blue-500 hover:text-blue-600"
                      >
                        <Users className="w-4 h-4 mr-1" />
                        <span>{group.usuarios_count}</span>
                      </button>
                    </td>
                    <td className="py-3">
                      {new Date(group.created_at).toLocaleDateString()}
                    </td>
                    <td className="py-3">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleEdit(group)}
                          className="p-1 text-blue-500 hover:bg-blue-100 rounded-full"
                          title="Editar grupo"
                          disabled={group.nombre === 'Grupo Estándar'}
                        >
                          <Edit2 className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => handleDelete(group.id)}
                          className="p-1 text-red-500 hover:bg-red-100 rounded-full"
                          title="Eliminar grupo"
                          disabled={group.nombre === 'Grupo Estándar'}
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {filteredGroups.length === 0 && (
                  <tr>
                    <td colSpan={6} className="text-center py-8 text-gray-500">
                      <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>No se encontraron grupos</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Modal para añadir/editar grupo */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">
              {selectedGroup ? 'Editar Grupo' : 'Nuevo Grupo'}
            </h2>
            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nombre
                </label>
                <input
                  type="text"
                  required
                  value={newGroup.nombre}
                  onChange={(e) => setNewGroup({...newGroup, nombre: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Descripción
                </label>
                <textarea
                  rows={3}
                  value={newGroup.descripcion}
                  onChange={(e) => setNewGroup({...newGroup, descripcion: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Rol del Grupo
                </label>
                <select
                  value={newGroup.role}
                  onChange={(e) => setNewGroup({...newGroup, role: e.target.value as UserGroup['role']})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  disabled={selectedGroup?.nombre === 'Grupo Estándar'}
                >
                  <option value="usuario_estandar">Usuario Estándar</option>
                  <option value="usuario_avanzado">Usuario Avanzado</option>
                  <option value="administrador">Administrador</option>
                </select>
              </div>
              <div className="flex justify-end space-x-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setSelectedGroup(null);
                  }}
                  className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-md"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
                  disabled={selectedGroup?.nombre === 'Grupo Estándar'}
                >
                  {selectedGroup ? 'Actualizar' : 'Crear'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal para gestionar usuarios del grupo */}
      {showUsersModal && selectedGroup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-4xl">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">
                Usuarios en {selectedGroup.nombre}
              </h2>
              <button
                onClick={() => {
                  setShowUsersModal(false);
                  setSelectedGroup(null);
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                ×
              </button>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {/* Lista de usuarios en el grupo */}
              <div>
                <h3 className="font-medium text-gray-700 mb-2">Usuarios en el grupo</h3>
                <div className="border rounded-lg divide-y">
                  {groupUsers.map(user => (
                    <div key={user.id} className="p-3 flex justify-between items-center">
                      <div>
                        <p className="font-medium">{user.nombre} {user.apellidos}</p>
                        <p className="text-sm text-gray-500">{user.email}</p>
                      </div>
                      <button
                        onClick={() => handleRemoveUserFromGroup(user.id, selectedGroup.id)}
                        className="p-1 text-red-500 hover:bg-red-100 rounded-full"
                        title="Eliminar del grupo"
                        disabled={selectedGroup.nombre === 'Grupo Estándar'}
                      >
                        <UserMinus className="w-5 h-5" />
                      </button>
                    </div>
                  ))}
                  {groupUsers.length === 0 && (
                    <p className="p-3 text-gray-500 text-center">No hay usuarios en este grupo</p>
                  )}
                </div>
              </div>

              {/* Lista de usuarios disponibles */}
              <div>
                <h3 className="font-medium text-gray-700 mb-2">Usuarios disponibles</h3>
                <div className="border rounded-lg divide-y">
                  {availableUsers.map(user => (
                    <div key={user.id} className="p-3 flex justify-between items-center">
                      <div>
                        <p className="font-medium">{user.nombre} {user.apellidos}</p>
                        <p className="text-sm text-gray-500">{user.email}</p>
                      </div>
                      <button
                        onClick={() => handleAddUserToGroup(user.id, selectedGroup.id)}
                        className="p-1 text-green-500 hover:bg-green-100 rounded-full"
                        title="Añadir al grupo"
                        disabled={selectedGroup.nombre === 'Grupo Estándar'}
                      >
                        <UserPlus className="w-5 h-5" />
                      </button>
                    </div>
                  ))}
                  {availableUsers.length === 0 && (
                    <p className="p-3 text-gray-500 text-center">No hay usuarios disponibles</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}